import Image from 'next/image'
import { useEffect } from 'react'
import type { Marble } from 'src/types/domain/Marble'
import type { LightningBoostType } from 'src/types/domain/Race'
import CheckSolidIcon from 'src/app/assets/icons/ico-check-solid.svg'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import { MarblePositionLabel } from 'src/components/MarblePositionLabel'
import { MarblePaneOddsValue } from './MarblePaneOddsValue'
import { MarblePaneMultiplier } from './MarblePaneMultiplier'
import { CoinFlipOddsValue } from './CoinFlipOddsValue'
import { IrlOddsValue } from './IrlOddsValue'
import { IrlAltOddsValue } from './IrlAltOddsValue'

type Props = Readonly<{
  gameType: string
  selectedMarbles: Marble[]
  marble: Marble
  selectedPositions: Record<string, number>
  isExactOrderSelected: boolean
  probabilities?: Record<string, number>
  multipliers?: Record<string, number>
  boostPercentage?: number
  isLightningBoosted?: boolean
  lightningBoostType?: LightningBoostType | null
  lightningBoostPercent?: number | null
  handleButtonClick: (callback: () => void) => void
  handleMarbleSelection: (marble: Marble) => void
}>

export function MarblePaneMarble({
  gameType,
  selectedMarbles,
  marble,
  selectedPositions,
  isExactOrderSelected,
  handleButtonClick,
  handleMarbleSelection,
  probabilities,
  multipliers,
  boostPercentage,
  isLightningBoosted,
  lightningBoostType,
  lightningBoostPercent
}: Props) {
  const isActive = selectedMarbles.some((m) => m.uuid === marble.uuid)

  useEffect(() => {
    if (marble.image) {
      const img = new window.Image()
      img.src = marble.image
    }
  }, [marble.image])

  // Calculate total boost including lightning boost for this marble
  const totalBoostWithLightning =
    isLightningBoosted && lightningBoostPercent
      ? (boostPercentage ?? 0) + lightningBoostPercent
      : boostPercentage

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- This element is clickable but does not have keyboard event handlers
    <div
      className={`marble-outer-container game-type-${gameType} ${isActive ? 'active' : ''} ${
        isLightningBoosted ? `lightning-boosted lightning-boosted--${lightningBoostType}` : ''
      }`}
      key={marble.uuid}
      onClick={() => {
        handleButtonClick(() => {
          handleMarbleSelection(marble)
        })
      }}
    >
      {isLightningBoosted && (
        <>
          <span className={`lightning-bg lightning-bg--${lightningBoostType}`} />
          <span className={`lightning-indicator lightning-indicator--${lightningBoostType}`}>
            <LightningIcon />
          </span>
        </>
      )}
      <div className="marble-details">
        <div className={`marble-outer ${isActive ? 'active' : ''}`}>
          <span
            className={`marble show-marble no-tooltip ${isActive ? 'active' : ''} marble-${
              marble.color
            }`}
          >
            {isActive ? (
              <Image
                alt={`Marble ${marble.number}`}
                className="marble-background-image"
                draggable="false"
                fill
                priority
                sizes="60px"
                src={marble.imageAlt ?? marble.image ?? ''}
              />
            ) : (
              <Image
                alt={`Marble ${marble.number}`}
                className="marble-background-image"
                draggable="false"
                fill
                priority
                sizes="60px"
                src={marble.image ?? ''}
              />
            )}
          </span>
          <span className="marble_blur" />
          <span className={`checkmark ${isActive ? 'active' : ''}`}>
            {isActive ? <CheckSolidIcon /> : null}
          </span>
          <div
            className={`marble-exact-order-position ${
              selectedPositions[marble.uuid] && isExactOrderSelected ? 'visible' : ''
            } ${
              selectedPositions[marble.uuid] === 1
                ? 'st'
                : selectedPositions[marble.uuid] === 2
                  ? 'nd'
                  : selectedPositions[marble.uuid] === 3
                    ? 'rd'
                    : ''
            }`}
          >
            <span>
              {selectedPositions[marble.uuid] !== undefined ? (
                <MarblePositionLabel position={selectedPositions[marble.uuid] ?? 0} />
              ) : (
                ''
              )}
            </span>
          </div>
        </div>
        <div className={`marble-odds ${isActive ? 'active' : ''}`}>
          {gameType === 'coin_flip' ? (
            <CoinFlipOddsValue marbleColor={marble.color} />
          ) : gameType === 'irl' ? (
            <IrlOddsValue marbleColor={marble.color} />
          ) : gameType === 'irl_alt' ||
            gameType === 'duck_river' ||
            gameType === 'snow_run' ||
            gameType === 'social_betting' ||
            gameType === 'crash' ? (
            <IrlAltOddsValue
              marbleColor={marble.color}
              marbleUuid={marble.uuid}
              multipliers={multipliers}
              boostPercentage={totalBoostWithLightning}
            />
          ) : gameType === 'stairpong' ||
            gameType === 'marbles_survivor' ||
            gameType === 'fishtank' ? (
            <MarblePaneMultiplier
              boostPercentage={totalBoostWithLightning}
              marbleUuid={marble.uuid}
              multipliers={multipliers}
            />
          ) : (
            <MarblePaneOddsValue marbleUuid={marble.uuid} probabilities={probabilities} />
          )}
        </div>
      </div>
    </div>
  )
}
